import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SelectOrgPrompt, useNeedsOrg } from '../components/SelectOrgPrompt';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Building2, Save, Loader2, CreditCard, Shield, Truck, Network, Crown, KeyRound, AlertTriangle, Mail } from 'lucide-react';
import axios from 'axios';
import BillingTab from '../components/BillingTab';

import { API_BASE as API } from '../config';

const ACCOUNT_TYPES = [
  { id: 'solo', label: 'V1 — Solo', desc: 'Single company', icon: Truck, color: 'amber' },
  { id: 'provider', label: 'V2 — Provider', desc: 'Multi-org', icon: Network, color: 'indigo' },
  { id: 'enterprise', label: 'V3 — Enterprise', desc: 'Insurance', icon: Crown, color: 'purple' },
];

export default function Settings() {
  const { user, hasRole, checkAuth, selectedTenants, operatorContext, setOperatorContext, resetOperatorContext, activeOrgId, userOrgs } = useAuth();
  const needsOrg = useNeedsOrg();
  const [searchParams, setSearchParams] = useSearchParams();

  const isOperator = user?.is_platform_operator;
  const inContext = isOperator && operatorContext?.tenantId;
  const hideBilling = isOperator && !inContext;
  const isProvider = user?.account_type === 'provider';
  const isInOrgView = isProvider && !!activeOrgId;
  const activeOrg = isInOrgView ? userOrgs.find(o => o.org_id === activeOrgId) : null;

  const initialTab = (!hideBilling && searchParams.get('tab') === 'billing') ? 'billing' : 'org';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Tenant settings state
  const [tenantName, setTenantName] = useState('');
  const [tenantLoading, setTenantLoading] = useState(true);
  const [tenantSaving, setTenantSaving] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [claimUpdateFreq, setClaimUpdateFreq] = useState('off');

  // Account type toggle
  const [switching, setSwitching] = useState(false);

  // Per-user email signature — appended to outbound claim emails.
  const [signature, setSignature] = useState(user?.email_signature || '');
  const [signatureSaving, setSignatureSaving] = useState(false);
  const SIGNATURE_MAX = 2048;
  useEffect(() => {
    // Re-sync when the user prop refreshes (e.g. after login or a checkAuth).
    setSignature(user?.email_signature || '');
  }, [user?.email_signature]);

  const handleSignatureSave = async (e) => {
    e?.preventDefault?.();
    setSignatureSaving(true);
    try {
      await axios.put(`${API}/auth/email-signature`, { email_signature: signature });
      toast.success(signature.trim() ? 'Signature saved' : 'Signature cleared');
      await checkAuth?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save signature');
    } finally {
      setSignatureSaving(false);
    }
  };

  // Permissions tab (Provider Admin only) — admin-toggleable feature flags
  const [permFlags, setPermFlags] = useState(null);
  const [permFlagSaving, setPermFlagSaving] = useState(null);
  const [permConfirmPrompt, setPermConfirmPrompt] = useState(null); // {key, label, currentlyEnabled}

  const isSuperAdmin = hasRole('super_admin');
  const canEditTenant = hasRole('admin', 'super_admin');
  const currentType = user?.account_type || 'solo';
  // Permissions tab is shown ONLY to Provider Admins (Solo + operator + non-admin all excluded)
  const showPermissionsTab = canEditTenant && isProvider && !(isOperator && !inContext);

  useEffect(() => {
    if (isOperator && !inContext) {
      setTenantLoading(false); // No tenant to load in AAC mode
      return;
    }
    fetchTenantSettings();
  }, [selectedTenants, operatorContext?.tenantId, activeOrgId]);

  // Sync tab with URL
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'billing') setActiveTab('billing');
    else if (tab === 'permissions' && showPermissionsTab) setActiveTab('permissions');
  }, [searchParams, showPermissionsTab]);

  // Auto-load admin-toggleable flags whenever the Permissions tab is opened
  useEffect(() => {
    if (activeTab !== 'permissions' || !showPermissionsTab) return;
    fetchPermissionFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, showPermissionsTab]);

  const fetchPermissionFlags = async () => {
    try {
      const res = await axios.get(`${API}/tenant/feature-flags`, { withCredentials: true });
      setPermFlags(res.data);
    } catch (err) {
      if (err.response?.status !== 403 && err.response?.status !== 404) {
        toast.error('Failed to load permissions');
      }
    }
  };

  const togglePermissionFlag = async (key, enabled) => {
    setPermFlagSaving(key);
    try {
      const res = await axios.put(
        `${API}/tenant/feature-flags`,
        { key, enabled },
        { withCredentials: true },
      );
      setPermFlags(res.data);
      toast.success(`${enabled ? 'Enabled' : 'Disabled'} ${key.replaceAll('_', ' ')}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update permission');
    } finally {
      setPermFlagSaving(null);
    }
  };

  const requestToggleFlag = (flag) => {
    if (flag.requires_confirm) {
      setPermConfirmPrompt({
        key: flag.key, label: flag.label, currentlyEnabled: flag.enabled,
        description: flag.description,
      });
      return;
    }
    togglePermissionFlag(flag.key, !flag.enabled);
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab === 'billing') {
      setSearchParams({ tab: 'billing' });
    } else {
      setSearchParams({});
    }
  };

  const fetchTenantSettings = async () => {
    try {
      const res = await axios.get(`${API}/tenant/settings`, { withCredentials: true });
      setTenantName(res.data.name);
      setTenantInfo(res.data);
      setClaimUpdateFreq(res.data.claim_update_frequency || 'off');
    } catch {
      toast.error('Failed to load organization settings');
    } finally {
      setTenantLoading(false);
    }
  };

  const handleTenantSave = async (e) => {
    e.preventDefault();
    if (!tenantName.trim()) {
      toast.error('Organization name cannot be empty');
      return;
    }
    setTenantSaving(true);
    try {
      await axios.put(`${API}/tenant/settings`, {
        name: tenantName.trim(),
        tenant_ids: selectedTenants.join(','),
        claim_update_frequency: claimUpdateFreq,
      }, { withCredentials: true });
      toast.success('Organization settings updated');
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update organization');
    } finally {
      setTenantSaving(false);
    }
  };

  const handleSwitchAccountType = async (newType) => {
    if (newType === currentType || switching) return;
    setSwitching(true);
    try {
      const res = await axios.post(`${API}/tenants/set-account-type`, { account_type: newType }, { withCredentials: true });
      toast.success(res.data.message);
      // Re-login to refresh JWT with new account_type
      await checkAuth();
      // Reload to pick up all UI changes (nav, dashboard, etc.)
      window.location.href = '/settings';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to switch account type');
    } finally {
      setSwitching(false);
    }
  };

  // Allow super_admins and operators through even when needsOrg is true
  if (needsOrg && !isSuperAdmin && !isOperator) return <SelectOrgPrompt />;

  return (
    <div className="min-h-screen bg-gray-200 p-4 md:p-6" data-testid="settings-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900" data-testid="settings-title">{isInOrgView ? `${activeOrg?.name || 'Organization'} — Settings` : 'Settings'}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isOperator && !inContext ? 'All About Claims — Settings' : inContext ? `${operatorContext.tenantName} — Settings` : 'Manage your organization and billing'}
          </p>
        </div>

        {/* Tab Switcher - hide billing tab for operator AAC mode */}
        {!hideBilling && (
        <div className="flex gap-1 bg-gray-300 p-1 rounded-lg" data-testid="settings-tabs">
          <button
            onClick={() => switchTab('org')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'org' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-800'
            }`}
            data-testid="tab-organization"
          >
            <Building2 className="w-4 h-4" />
            Organization
          </button>
          <button
            onClick={() => switchTab('billing')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'billing' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-800'
            }`}
            data-testid="tab-billing"
          >
            <CreditCard className="w-4 h-4" />
            Billing
          </button>
          {showPermissionsTab && (
            <button
              onClick={() => switchTab('permissions')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'permissions' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-800'
              }`}
              data-testid="tab-permissions"
            >
              <KeyRound className="w-4 h-4" />
              Permissions
            </button>
          )}
        </div>
        )}

        {/* Organization Tab */}
        {activeTab === 'org' && (
          <Card data-testid="org-settings-card">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-teal-500" />
                <CardTitle className="text-lg">Organization</CardTitle>
              </div>
              <CardDescription>
                {canEditTenant ? 'Update your organization name' : 'View your organization details'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {tenantLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="org-loading">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                </div>
              ) : (
                <form onSubmit={handleTenantSave} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="org-name">Organization Name</Label>
                    <Input
                      id="org-name"
                      value={tenantName}
                      onChange={(e) => setTenantName(e.target.value)}
                      disabled={!canEditTenant}
                      placeholder="Enter organization name"
                      data-testid="org-name-input"
                    />
                  </div>
                  {tenantInfo && (
                    <div className="grid grid-cols-2 gap-4 text-sm" data-testid="org-info">
                      <div>
                        <span className="text-gray-500">Tenant ID</span>
                        <p className="font-mono text-xs text-gray-700 mt-0.5">{tenantInfo.tenant_id}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Members</span>
                        <p className="text-gray-700 mt-0.5">{tenantInfo.user_count}</p>
                      </div>
                    </div>
                  )}
                  {canEditTenant && !hideBilling && (
                    <div className="space-y-1.5 pt-2 border-t border-gray-100">
                      <Label htmlFor="claim-freq">Claim Update Policy</Label>
                      <p className="text-xs text-gray-500">How often must open claims (New & In Process) be updated?</p>
                      <select
                        id="claim-freq"
                        value={claimUpdateFreq}
                        onChange={(e) => setClaimUpdateFreq(e.target.value)}
                        className="w-full h-9 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        data-testid="claim-update-freq-select"
                      >
                        <option value="off">Off — no tracking</option>
                        <option value="daily">Daily</option>
                        <option value="3_days">Every 3 days</option>
                        <option value="1_week">Every week</option>
                        <option value="2_weeks">Every 2 weeks</option>
                        <option value="30_days">Every 30 days</option>
                      </select>
                    </div>
                  )}
                  {canEditTenant && (
                    <div className="mt-4 mb-4 flex justify-end">
                      <Button type="submit" className="bg-teal-500 hover:bg-teal-600" disabled={tenantSaving} data-testid="org-save-btn">
                        {tenantSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
                      </Button>
                    </div>
                  )}
                </form>
              )}
            </CardContent>
          </Card>
        )}

        {/* Email Signature — per-user, visible on the Organization tab so it
            sits with the other "personal-ish" settings. */}
        {activeTab === 'org' && (
          <Card data-testid="email-signature-card">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-teal-500" />
                <CardTitle className="text-lg">Email Signature</CardTitle>
              </div>
              <CardDescription>
                Auto-appended to claim emails you send from CMS. Plain text — line breaks are preserved.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSignatureSave} className="space-y-3">
                <textarea
                  value={signature}
                  onChange={(e) => setSignature(e.target.value.slice(0, SIGNATURE_MAX))}
                  rows={6}
                  placeholder={`e.g.\n\n${user?.name || 'Your Name'}\nClaims Adjuster · All About Claims\n(555) 555-1212`}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono"
                  data-testid="email-signature-textarea"
                />
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">
                    {signature.length}/{SIGNATURE_MAX} characters
                  </span>
                  <Button
                    type="submit"
                    size="sm"
                    className="bg-teal-500 hover:bg-teal-600"
                    disabled={signatureSaving}
                    data-testid="email-signature-save-btn"
                  >
                    {signatureSaving ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" /> Save Signature</>
                    )}
                  </Button>
                </div>
                {signature.trim() && (
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3" data-testid="email-signature-preview">
                    <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-1.5">Preview</p>
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{signature}</pre>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        )}

        {/* Billing Tab - only for regular tenants or operator in context */}
        {activeTab === 'billing' && !hideBilling && <BillingTab />}

        {/* Permissions Tab — Provider Admin self-serve feature flags */}
        {activeTab === 'permissions' && showPermissionsTab && (
          <Card data-testid="permissions-card">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-teal-500" />
                <CardTitle className="text-lg">Permissions</CardTitle>
              </div>
              <CardDescription>
                Self-serve toggles for tenant-wide permission features. Changes apply immediately for all users in {permFlags?.tenant_name || 'this tenant'}.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!permFlags ? (
                <div className="py-6 text-center text-sm text-gray-400 flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : permFlags.flags.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No tenant-toggleable permissions are currently available for your account type.</p>
              ) : (
                <div className="space-y-2.5">
                  {permFlags.flags.map((f) => {
                    const saving = permFlagSaving === f.key;
                    const muted = !f.applicable;
                    return (
                      <div
                        key={f.key}
                        className={`flex items-start gap-3 p-3 rounded-lg border ${
                          muted ? 'border-gray-200 bg-gray-50 opacity-60' : f.enabled ? 'border-teal-200 bg-teal-50/40' : 'border-gray-200 bg-white'
                        }`}
                        data-testid={`perm-flag-row-${f.key}`}
                      >
                        <button
                          type="button"
                          disabled={muted || saving}
                          onClick={() => requestToggleFlag(f)}
                          className={`mt-0.5 relative w-10 h-5 rounded-full transition-colors shrink-0 ${
                            f.enabled ? 'bg-teal-500' : 'bg-gray-300'
                          } ${muted ? 'cursor-not-allowed' : ''}`}
                          data-testid={`perm-flag-toggle-${f.key}`}
                          aria-label={`Toggle ${f.label}`}
                        >
                          <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${f.enabled ? 'translate-x-5' : ''}`} />
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-sm font-medium text-gray-900">{f.label}</span>
                            {f.provider_only && (
                              <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200">Provider</span>
                            )}
                            {f.requires_confirm && (
                              <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">Confirm</span>
                            )}
                            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                          </div>
                          <p className="text-xs text-gray-500 leading-snug mt-1">
                            {muted ? `Not applicable for ${permFlags.account_type} tenants. ` : ''}
                            {f.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Confirmation modal for risky flags */}
        {permConfirmPrompt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="perm-confirm-modal">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5 space-y-4">
              <div className="flex items-start gap-3">
                <div className="shrink-0 mt-0.5 p-1.5 rounded-full bg-amber-100">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    {permConfirmPrompt.currentlyEnabled ? 'Disable' : 'Enable'} "{permConfirmPrompt.label}"?
                  </h3>
                  <p className="text-xs text-gray-600 mt-1.5 leading-snug">
                    {permConfirmPrompt.description}
                  </p>
                  <p className="text-xs text-gray-500 mt-2 italic">
                    This change applies immediately to all users in your tenant.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setPermConfirmPrompt(null)} data-testid="perm-confirm-cancel">
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className={permConfirmPrompt.currentlyEnabled ? 'bg-red-500 hover:bg-red-600' : 'bg-teal-500 hover:bg-teal-600'}
                  onClick={async () => {
                    const target = !permConfirmPrompt.currentlyEnabled;
                    const key = permConfirmPrompt.key;
                    setPermConfirmPrompt(null);
                    await togglePermissionFlag(key, target);
                  }}
                  data-testid="perm-confirm-ok"
                >
                  {permConfirmPrompt.currentlyEnabled ? 'Yes, disable' : 'Yes, enable'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Type Selector — Platform Operator only */}
        {activeTab === 'org' && isOperator && !inContext && (
          <Card data-testid="subscription-type-card">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-teal-500" />
                <CardTitle className="text-lg">Browse as Subscription Type</CardTitle>
              </div>
              <CardDescription>Select a subscription tier to browse and manage organizations of that type</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3" data-testid="subscription-type-toggle">
                {ACCOUNT_TYPES.map(({ id, label, desc, icon: Icon, color }) => {
                  const apiType = id === 'solo' ? 'solo' : id === 'provider' ? 'provider' : 'enterprise';
                  const isActive = operatorContext?.accountType === apiType;
                  const colors = {
                    amber:  { border: 'border-amber-400',  bg: 'bg-amber-50',   text: 'text-amber-700',  icon: 'text-amber-500' },
                    indigo: { border: 'border-indigo-400', bg: 'bg-indigo-50',  text: 'text-indigo-700', icon: 'text-indigo-500' },
                    purple: { border: 'border-purple-400', bg: 'bg-purple-50',  text: 'text-purple-700', icon: 'text-purple-500' },
                  }[color];
                  return (
                    <button
                      key={id}
                      onClick={() => {
                        if (isActive) {
                          resetOperatorContext();
                        } else {
                          setOperatorContext({ accountType: apiType, tenantId: null, tenantName: null, orgId: null, orgName: null });
                        }
                      }}
                      className={`relative flex flex-col items-center gap-1.5 p-4 rounded-xl border-2 transition-all ${
                        isActive
                          ? `${colors.border} ${colors.bg} shadow-sm`
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      data-testid={`subscription-type-${id}`}
                    >
                      {isActive && (
                        <span className={`absolute -top-2 right-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                          Selected
                        </span>
                      )}
                      <Icon className={`w-6 h-6 ${isActive ? colors.icon : 'text-gray-400'}`} />
                      <span className={`text-sm font-semibold ${isActive ? colors.text : 'text-gray-600'}`}>{label}</span>
                      <span className="text-[10px] text-gray-400">{desc}</span>
                    </button>
                  );
                })}
              </div>
              {operatorContext?.accountType && (
                <p className="text-xs text-teal-600 mt-3 text-center font-medium">
                  Use "Select Organization" in the user menu to pick an organization
                </p>
              )}
              <p className="text-[11px] text-gray-400 mt-2 text-center">
                Click a selected type again to deselect
              </p>
            </CardContent>
          </Card>
        )}

        {/* Account Type Toggle — Super Admin only, hide for operators */}
        {activeTab === 'org' && isSuperAdmin && !isOperator && (
          <Card data-testid="account-type-card">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-500" />
                <CardTitle className="text-lg">Account Type</CardTitle>
              </div>
              <CardDescription>Super Admin: Switch between plan tiers to test different experiences</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3" data-testid="account-type-toggle">
                {ACCOUNT_TYPES.map(({ id, label, desc, icon: Icon, color }) => {
                  const isActive = currentType === id;
                  const colors = {
                    amber:  { border: 'border-amber-400',  bg: 'bg-amber-50',   text: 'text-amber-700',  icon: 'text-amber-500' },
                    indigo: { border: 'border-indigo-400', bg: 'bg-indigo-50',  text: 'text-indigo-700', icon: 'text-indigo-500' },
                    purple: { border: 'border-purple-400', bg: 'bg-purple-50',  text: 'text-purple-700', icon: 'text-purple-500' },
                  }[color];
                  return (
                    <button
                      key={id}
                      onClick={() => handleSwitchAccountType(id)}
                      disabled={switching}
                      className={`relative flex flex-col items-center gap-1.5 p-4 rounded-xl border-2 transition-all ${
                        isActive
                          ? `${colors.border} ${colors.bg} shadow-sm`
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      } ${switching ? 'opacity-50 cursor-wait' : ''}`}
                      data-testid={`account-type-${id}`}
                    >
                      {isActive && (
                        <span className={`absolute -top-2 right-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                          Active
                        </span>
                      )}
                      <Icon className={`w-6 h-6 ${isActive ? colors.icon : 'text-gray-400'}`} />
                      <span className={`text-sm font-semibold ${isActive ? colors.text : 'text-gray-600'}`}>{label}</span>
                      <span className="text-[10px] text-gray-400">{desc}</span>
                    </button>
                  );
                })}
              </div>
              {switching && (
                <div className="flex items-center justify-center gap-2 mt-3 text-sm text-gray-500" data-testid="account-type-switching">
                  <Loader2 className="w-4 h-4 animate-spin" /> Switching account type...
                </div>
              )}
              <p className="text-[11px] text-gray-400 mt-3 text-center">
                Switching updates your subscription limits and unlocks/restricts features accordingly.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
